import React from 'react';
import { SchematicShell, Line, Circle, Rect } from './SchematicPrimitives';

const IN = 0.0254;

// ── Tower Fan Schematic ──

export function DormTowerFanSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const isSide = view === 'left' || view === 'right';
  const w = isSide ? depth : width;

  const baseH = height * 0.08;
  const columnH = height * 0.35;
  const airflowH = height - baseH - columnH;
  const bridgeH = height * 0.05;
  const pillarFrac = 0.18;
  const pillarW = w * pillarFrac;

  // Grille bars in the airflow opening
  const grilleInsetY = 0.8 * IN;
  const grilleRegionH = airflowH - bridgeH - grilleInsetY * 2;
  const numBars = Math.max(5, Math.round(grilleRegionH / (1.0 * IN)));
  const barSpacing = grilleRegionH / numBars;
  const barInset = pillarW + 0.1 * IN;

  const bars = [];
  for (let i = 0; i < numBars; i++) {
    const y = bridgeH + grilleInsetY + barSpacing * (i + 0.5);
    bars.push(
      <Line
        key={`bar-${i}`}
        x1={barInset}
        y1={y}
        x2={w - barInset}
        y2={y}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />
    );
  }

  // Control panel indicator on front view only
  const controlPanel = !isSide ? (
    <Rect
      x={w * 0.2}
      y={height - baseH - columnH * 0.25}
      width={w * 0.6}
      height={1.2 * IN}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth * 0.5}
    />
  ) : null;

  return (
    <SchematicShell
      width={w}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Top bridge divider */}
      <Line
        x1={0}
        y1={bridgeH}
        x2={w}
        y2={bridgeH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Pillar left */}
      <Line
        x1={pillarW}
        y1={bridgeH}
        x2={pillarW}
        y2={height - baseH - columnH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Pillar right */}
      <Line
        x1={w - pillarW}
        y1={bridgeH}
        x2={w - pillarW}
        y2={height - baseH - columnH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Column / airflow divider */}
      <Line
        x1={0}
        y1={height - baseH - columnH}
        x2={w}
        y2={height - baseH - columnH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Base divider */}
      <Line
        x1={0}
        y1={height - baseH}
        x2={w}
        y2={height - baseH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {bars}
      {controlPanel}
    </SchematicShell>
  );
}

DormTowerFanSchematic.supportsSideViews = true;

// ── Desk Fan Schematic ──

export function DormDeskFanSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const isSide = view === 'left' || view === 'right';
  const w = isSide ? depth : width;

  const baseH = height * 0.08;
  const neckH = height * 0.18;
  const headR = Math.min(width, depth) * 0.42;
  const headCenterY = height - baseH - neckH - headR;
  const neckW = Math.min(width, depth) * 0.12;
  const baseW = w * 0.75;
  const baseLeft = (w - baseW) / 2;

  if (isSide) {
    const headD = depth * 0.25;

    return (
      <SchematicShell
        width={w}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        drawFrame={false}
      >
        {/* Base */}
        <Rect
          x={baseLeft}
          y={height - baseH}
          width={baseW}
          height={baseH}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
        {/* Neck */}
        <Line
          x1={w / 2 - neckW / 2}
          y1={height - baseH - neckH}
          x2={w / 2 - neckW / 2}
          y2={height - baseH}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
        <Line
          x1={w / 2 + neckW / 2}
          y1={height - baseH - neckH}
          x2={w / 2 + neckW / 2}
          y2={height - baseH}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
        {/* Fan head — side view shows the depth as a rectangle */}
        <Rect
          x={(w - headD) / 2}
          y={headCenterY - headR}
          width={headD}
          height={headR * 2}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      </SchematicShell>
    );
  }

  // Front view — circular fan head
  const hubR = headR * 0.25;
  const cx = w / 2;

  return (
    <SchematicShell
      width={w}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {/* Base */}
      <Rect
        x={baseLeft}
        y={height - baseH}
        width={baseW}
        height={baseH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Neck */}
      <Line
        x1={cx - neckW / 2}
        y1={height - baseH - neckH}
        x2={cx - neckW / 2}
        y2={height - baseH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={cx + neckW / 2}
        y1={height - baseH - neckH}
        x2={cx + neckW / 2}
        y2={height - baseH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Fan head — outer circle */}
      <Circle
        cx={cx}
        cy={headCenterY}
        r={headR}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Center hub */}
      <Circle
        cx={cx}
        cy={headCenterY}
        r={hubR}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.7}
      />
      {/* Blade lines — four blades from hub to near outer ring */}
      {[0, 1, 2, 3].map((i) => {
        const angle = Math.PI / 4 + (i * Math.PI) / 2;
        return (
          <Line
            key={`blade-${i}`}
            x1={cx + Math.cos(angle) * hubR}
            y1={headCenterY + Math.sin(angle) * hubR}
            x2={cx + Math.cos(angle) * headR * 0.85}
            y2={headCenterY + Math.sin(angle) * headR * 0.85}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth * 0.6}
          />
        );
      })}
    </SchematicShell>
  );
}

DormDeskFanSchematic.supportsSideViews = true;
